import { useQuery } from '@tanstack/react-query'
import apiClient from '@/api/client'
import { loadBrandLogo } from '@/lib/brandedQr'

/**
 * The tenant logo used in the centre of branded QR codes, already resolved to
 * a data: URL and ready to hand to `buildBrandedQrOptions`.
 *
 * Source is `receipt_logo_url` — the same logo that prints on receipts, so
 * turning branded QRs on requires no extra setup from the restaurant. The
 * companion `branded_qr_enabled` setting is the tenant-level switch for the
 * surfaces that have no per-item style row (enrolment, tables, receipts,
 * posters); the Marketing QR tab overrides it per code.
 *
 * Cache key matches AdminSidebar / useBusinessName so every caller shares one
 * React Query entry.
 */
export function useBrandQrLogo(): {
  logoDataUrl: string | null
  brandedQrEnabled: boolean
  logoUrl: string
} {
  const { data: settingsRes } = useQuery({
    queryKey: ['settings', 'all'],
    queryFn: () => apiClient.getAllSettings(),
    staleTime: 1000 * 60 * 5,
  })

  const raw = settingsRes?.data as Record<string, unknown> | undefined
  const logoUrl = ((raw?.receipt_logo_url as string | undefined) || '').trim()
  // Defaults to on: a tenant who has configured a logo gets branded codes
  // without hunting for a switch. Only newly generated images change — the
  // encoded URL is untouched, so QRs already printed and stuck on tables keep
  // resolving exactly as before.
  const brandedQrEnabled = raw?.branded_qr_enabled !== false

  const { data: logoDataUrl } = useQuery({
    queryKey: ['brand-qr-logo', logoUrl],
    queryFn: () => loadBrandLogo(logoUrl),
    enabled: Boolean(logoUrl),
    staleTime: Infinity,
  })

  return { logoDataUrl: logoDataUrl ?? null, brandedQrEnabled, logoUrl }
}

/**
 * Variant for surfaces that are handed an explicit logo rather than reading
 * the setting themselves — the poster studio passes a `PosterBrand` down, and
 * its export path receives the same object, so both must resolve from that one
 * value or the printed poster can drift from the approved preview.
 */
export function useResolvedQrLogo(logoUrl: string | undefined): {
  logoDataUrl: string | null
  brandedQrEnabled: boolean
} {
  const { data: settingsRes } = useQuery({
    queryKey: ['settings', 'all'],
    queryFn: () => apiClient.getAllSettings(),
    staleTime: 1000 * 60 * 5,
  })
  const raw = settingsRes?.data as Record<string, unknown> | undefined
  const brandedQrEnabled = raw?.branded_qr_enabled !== false

  const src = (logoUrl || '').trim()
  const { data: logoDataUrl } = useQuery({
    queryKey: ['brand-qr-logo', src],
    queryFn: () => loadBrandLogo(src),
    enabled: Boolean(src),
    staleTime: Infinity,
  })

  return { logoDataUrl: logoDataUrl ?? null, brandedQrEnabled }
}
